import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { X, Send, Trophy, MessageSquare, Users, Radio, Loader2, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/auth";

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 5000;

function fmtTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

function fmtCountdown(ms) {
  if (ms <= 0) return "AGORA";
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m.toString().padStart(2, "0")}m ${sec.toString().padStart(2, "0")}s`;
  if (m > 0) return `${m}m ${sec.toString().padStart(2, "0")}s`;
  return `${sec}s`;
}

/** Interactive lobby: chat + live leaderboard. Polls both every 5s. */
export default function RoomLobbyModal({ open, room, myParticipation, onClose }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("chat");
  const scrollerRef = useRef(null);
  const lastIsoRef = useRef(null);

  const isParticipant = !!myParticipation;

  const loadChat = useCallback(async () => {
    if (!room) return;
    try {
      const url = new URL(`${API}/api/rooms/${room.room_id}/chat`);
      if (lastIsoRef.current) url.searchParams.set("since", lastIsoRef.current);
      const r = await fetch(url.toString());
      if (!r.ok) return;
      const d = await r.json();
      const incoming = d.messages || [];
      if (incoming.length) {
        setMessages((prev) => {
          const combined = [...prev, ...incoming];
          // Dedupe by message_id, cap 200
          const seen = new Set();
          const dedup = [];
          for (const m of combined) {
            if (!seen.has(m.message_id)) { seen.add(m.message_id); dedup.push(m); }
          }
          return dedup.slice(-200);
        });
        lastIsoRef.current = incoming[incoming.length - 1].created_at;
      } else if (!lastIsoRef.current) {
        // First load returned zero — mark server_time so next poll uses `since`
        lastIsoRef.current = d.server_time;
      }
    } catch { /* silent */ }
  }, [room]);

  const loadLeaderboard = useCallback(async () => {
    if (!room) return;
    try {
      const r = await fetch(`${API}/api/rooms/${room.room_id}/leaderboard`);
      if (!r.ok) return;
      const d = await r.json();
      setLeaderboard(d.leaderboard || []);
    } catch { /* silent */ }
  }, [room]);

  // Reset on open & start polling
  useEffect(() => {
    if (!open || !room) return;
    setMessages([]);
    setLeaderboard([]);
    lastIsoRef.current = null;
    setErr(null);
    loadChat();
    loadLeaderboard();
    const tc = setInterval(loadChat, POLL_MS);
    const tl = setInterval(loadLeaderboard, POLL_MS);
    return () => { clearInterval(tc); clearInterval(tl); };
  }, [open, room, loadChat, loadLeaderboard]);

  // Auto-scroll to latest chat
  useEffect(() => {
    if (tab === "chat" && scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, tab]);

  const send = useCallback(async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true); setErr(null);
    try {
      const r = await fetch(`${API}/api/rooms/${room.room_id}/chat`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || "Falha ao enviar");
      setDraft("");
      loadChat();
    } catch (e) { setErr(e.message); }
    finally { setSending(false); }
  }, [draft, room, sending, loadChat]);

  const myRank = useMemo(() => {
    if (!user) return null;
    const idx = leaderboard.findIndex((p) => p.user_id === user.user_id);
    return idx >= 0 ? idx + 1 : null;
  }, [leaderboard, user]);

  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [open]);

  const roomStatus = useMemo(() => {
    if (!room) return null;
    const start = new Date(room.scheduled_start).getTime();
    const end = new Date(room.scheduled_end).getTime();
    const msToStart = start - nowTick;
    const msToEnd = end - nowTick;
    if (msToEnd <= 0) return { phase: "ended" };
    if (msToStart > 0) return { phase: "upcoming", msToStart };
    return { phase: "active", msToEnd };
  }, [room, nowTick]);

  if (!open || !room) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4" data-testid="room-lobby-modal">
      <div className="w-full max-w-2xl h-[80vh] rounded-sm border border-primary/40 bg-gradient-to-b from-[#050a18] to-[#020408] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-primary/20">
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] text-secondary flex items-center gap-2">
              <Radio className="h-3 w-3 animate-pulse" /> LOBBY DA SALA
            </p>
            <h2 className="font-display font-black text-2xl tracking-tighter text-white">{room.label}</h2>
          </div>
          <div className="flex items-center gap-3">
            <div className="font-mono text-[10px] tracking-widest text-muted-foreground flex items-center gap-1">
              <Users className="h-3 w-3" /> <span className="text-white">{room.player_count}/{room.capacity}</span>
            </div>
            <button onClick={onClose} data-testid="room-lobby-close"
              className="p-2 border border-primary/30 rounded-sm hover:bg-primary/10 text-primary">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Status strip — clearly distinguishes "starts in" vs "ends in" */}
        {roomStatus && roomStatus.phase !== "ended" && (
          <div
            data-testid="room-lobby-status"
            className={
              roomStatus.phase === "active"
                ? "flex items-center justify-center gap-2 py-2 px-4 border-b border-emerald-400/40 bg-emerald-500/10 font-mono text-[11px] tracking-widest"
                : "flex items-center justify-center gap-2 py-2 px-4 border-b border-orange-400/40 bg-orange-500/10 font-mono text-[11px] tracking-widest"
            }
          >
            {roomStatus.phase === "active" ? (
              <>
                <Radio className="h-3.5 w-3.5 text-emerald-300 animate-pulse" />
                <span className="text-emerald-200 font-black">{t("room_lobby.live_label")}</span>
                <span className="text-white/50">·</span>
                <Clock className="h-3.5 w-3.5 text-emerald-200" />
                <span className="text-white">{t("room_lobby.ends_in_label")} <span className="text-emerald-200 font-bold">{fmtCountdown(roomStatus.msToEnd)}</span></span>
              </>
            ) : (
              <>
                <Clock className={`h-3.5 w-3.5 ${roomStatus.msToStart < 60_000 ? "text-red-300 animate-pulse" : "text-orange-300"}`} />
                <span className="text-orange-200 font-black">{t("room_lobby.waiting")}</span>
                <span className="text-white/50">·</span>
                <span className="text-white">{t("room_lobby.starts_in_label")} <span className={roomStatus.msToStart < 60_000 ? "text-red-300 animate-pulse font-bold" : "text-orange-200 font-bold"}>{fmtCountdown(roomStatus.msToStart)}</span></span>
              </>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-primary/20 font-mono text-[10px] tracking-widest">
          <button onClick={() => setTab("chat")} data-testid="room-lobby-tab-chat"
            className={`flex-1 px-4 py-2 inline-flex items-center justify-center gap-2 ${tab === "chat" ? "bg-primary/10 text-primary border-b-2 border-primary" : "text-muted-foreground hover:text-white"}`}>
            <MessageSquare className="h-3 w-3" /> {t("room_lobby.tabs.chat")}
          </button>
          <button onClick={() => setTab("ranking")} data-testid="room-lobby-tab-ranking"
            className={`flex-1 px-4 py-2 inline-flex items-center justify-center gap-2 ${tab === "ranking" ? "bg-secondary/10 text-secondary border-b-2 border-secondary" : "text-muted-foreground hover:text-white"}`}>
            <Trophy className="h-3 w-3" /> {t("room_lobby.tabs.leaderboard")} {myRank ? `· ${myRank}º` : ""}
          </button>
        </div>

        {/* Body */}
        {tab === "chat" ? (
          <>
            <div ref={scrollerRef} className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="room-lobby-messages">
              {messages.length === 0 ? (
                <p className="text-center text-xs font-mono text-muted-foreground py-8">
                  {t("room_lobby.chat_empty")}
                </p>
              ) : messages.map((m) => (
                <div key={m.message_id} data-testid={`room-chat-msg-${m.message_id}`}
                  className="rounded-sm border border-primary/10 bg-black/40 px-3 py-2">
                  <div className="flex items-baseline justify-between gap-2 mb-1">
                    <span className="font-mono text-[10px] tracking-widest text-secondary">{m.display_name}</span>
                    <span className="font-mono text-[9px] text-muted-foreground">{fmtTime(m.created_at)}</span>
                  </div>
                  <p className="text-sm text-white/90 break-words">{m.text}</p>
                </div>
              ))}
            </div>
            <div className="p-3 border-t border-primary/20">
              {!user ? (
                <p className="text-xs text-center font-mono text-muted-foreground">{t("room_lobby.chat_login")}</p>
              ) : !isParticipant ? (
                <p className="text-xs text-center font-mono text-muted-foreground">{t("room_lobby.chat_not_participant")}</p>
              ) : (
                <>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                      placeholder={t("room_lobby.chat_placeholder")}
                      maxLength={240}
                      data-testid="room-chat-input"
                      className="flex-1 bg-black/60 border border-primary/30 rounded-sm px-3 py-2 font-mono text-xs text-white placeholder-muted-foreground/60" />
                    <button onClick={send} disabled={sending || !draft.trim()}
                      data-testid="room-chat-send"
                      className="inline-flex items-center gap-1 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-mono tracking-widest text-[10px] px-3 py-2 disabled:opacity-40">
                      {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                    </button>
                  </div>
                  {err && <p className="mt-1 text-[10px] font-mono text-red-400">{err}</p>}
                </>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 overflow-y-auto p-3" data-testid="room-lobby-leaderboard">
            {leaderboard.length === 0 ? (
              <p className="text-center text-xs font-mono text-muted-foreground py-8">
                {t("room_lobby.leaderboard_empty")}
              </p>
            ) : (
              <table className="w-full font-mono text-xs">
                <thead>
                  <tr className="text-muted-foreground text-[10px] tracking-widest border-b border-primary/20">
                    <th className="text-left py-2 px-2">#</th>
                    <th className="text-left py-2 px-2">PILOTO</th>
                    <th className="text-right py-2 px-2">NÍVEL</th>
                    <th className="text-right py-2 px-2">PONTOS</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((p, i) => {
                    const isMe = user && p.user_id === user.user_id;
                    return (
                      <tr key={p.user_id}
                        data-testid={`room-lobby-rank-${i + 1}`}
                        className={`border-b border-primary/10 ${isMe ? "bg-primary/10" : ""}`}>
                        <td className="py-2 px-2 text-secondary font-bold">{i + 1}</td>
                        <td className="py-2 px-2 text-white">
                          {p.display_name}{isMe ? " (você)" : ""}
                        </td>
                        <td className="py-2 px-2 text-right text-muted-foreground">L{p.best_level || 0}</td>
                        <td className="py-2 px-2 text-right text-primary font-bold">{(p.best_score || 0).toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            <p className="text-center text-[10px] font-mono text-muted-foreground mt-4">
              Atualiza a cada {POLL_MS / 1000}s · Só a melhor pontuação de cada piloto conta
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
